import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, LogOut, Plus, Trophy, Shield, Bell, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import SubAccountPicker from '../components/SubAccountPicker';

// ── One-time ActiveStudent passcode activation card ───────────────────────────
function StudentActivationCard({ onActivated }) {
  const [open,      setOpen]      = useState(false);
  const [code,      setCode]      = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');

  const handleActivate = async () => {
    if (!code.trim()) { setError('Please enter your passcode'); return; }
    setLoading(true);
    setError('');
    try {
      const r = await api.activateStudentPasscode(code.trim());
      toast.success(r.message || 'Student access activated!');
      onActivated();
    } catch (e) {
      setError(e.message || 'Invalid passcode');
    }
    setLoading(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ margin: '0 16px 12px', width: 'calc(100% - 32px)', background: 'var(--pp-surface)', border: '1px dashed rgba(41,121,255,0.4)', padding: 14, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
        <Key size={20} color="var(--pp-accent)" style={{ flexShrink: 0 }} />
        <div>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--pp-accent)' }}>Have an ActiveStudent Passcode?</p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>Enter it once to unlock student access & funded accounts</p>
        </div>
        <ChevronRight size={16} color="var(--pp-accent)" style={{ marginLeft: 'auto', flexShrink: 0 }} />
      </button>
    );
  }

  return (
    <div style={{ margin: '0 16px 12px', background: 'var(--pp-surface)', border: '1px solid rgba(41,121,255,0.4)', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--pp-accent)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Key size={16} /> Activate Student Access
        </p>
        <button onClick={() => { setOpen(false); setCode(''); setError(''); }}
          style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 20 }}>×</button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--pp-text-3)', lineHeight: 1.6 }}>
        Enter the ActiveStudent Passcode provided by your Profit Pulse contact.
        This unlocks funded accounts and full strategy access.
      </p>
      <div style={{ display: 'flex', gap: 8, marginBottom: error ? 8 : 0 }}>
        <input
          value={code}
          onChange={e => { setCode(e.target.value); setError(''); }}
          placeholder="AS-XXXXXXXX"
          style={{ flex: 1, padding: '11px 14px', background: 'var(--pp-overlay)', border: `1px solid ${error ? '#FF3333' : 'var(--pp-accent)'}`, color: 'var(--pp-text)', fontSize: 15, fontFamily: 'monospace', letterSpacing: '0.15em', outline: 'none' }}
          onKeyDown={e => e.key === 'Enter' && handleActivate()}
        />
        <button onClick={handleActivate} disabled={loading || !code.trim()}
          style={{ background: loading || !code.trim() ? 'var(--pp-overlay)' : '#2979FF', border: 'none', color: '#fff', padding: '0 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
          {loading ? '...' : 'Activate'}
        </button>
      </div>
      {error && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#FF3333' }}>{error}</p>}
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [brokerLinks, setBrokerLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userMode, setUserMode] = useState('simple');
  const [userStats, setUserStats] = useState(null);
  const [mt5Connections, setMT5Connections] = useState([]);
  const [tlConnections, setTLConnections] = useState([]);
  const [tradovateConnections, setTradovateConnections] = useState([]);
  const [mtConnections, setMTConnections] = useState([]);
  const [volConnections, setVolConnections] = useState([]);
  const [coConnections, setCoConnections] = useState([]);
  const [metaapiAccounts, setMetaapiAccounts] = useState([]);
  const [onboardingEnabled, setOnboardingEnabled] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [subs, links, mode, stats, mt5, tl, trad, mt, vol, co, platformSettings] = await Promise.all([
        api.getUserSubscriptions(),
        api.getBrokerLinks(),
        api.getUserMode().catch(() => ({ mode: 'simple' })),
        api.getUserStats().catch(() => null),
        api.getMT5Connections().catch(() => []),
        api.getTradeLockerConnections().catch(() => []),
        api.getTradovateConnections().catch(() => []),
        api.getMatchTraderConnections().catch(() => []),
        api.listVolumetrica().catch(() => []),
        api.listCloseOption().catch(() => []),
        api.getPublicPlatformSettings().catch(() => ({ onboarding_wizard_enabled: false })),
      ]);
      setSubscriptions(subs);
      setBrokerLinks(links);
      setUserMode(mode.mode || 'simple');
      setUserStats(stats);
      setMT5Connections(mt5 || []);
      setTLConnections(tl || []);
      setTradovateConnections(trad || []);
      setMTConnections(mt || []);
      setVolConnections(vol || []);
      setCoConnections(co || []);
      setOnboardingEnabled(platformSettings?.onboarding_wizard_enabled === true);
      api.getMetaAPISlaveBalances().then(r => setMetaapiAccounts(r?.accounts || [])).catch(() => {});
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const handleToggleMode = async () => {
    const newMode = userMode === 'simple' ? 'advanced' : 'simple';
    setUserMode(newMode);
    await api.setUserMode(newMode).catch(console.error);
  };

  const StatusBadge = ({ status }) => {
    const cls = { active: 'badge-success', connected: 'badge-success', connecting: 'badge-warning', pending: 'badge-warning', error: 'badge-error' };
    return <span className={cls[status] || 'badge-accent'}>{(status || 'ACTIVE').toUpperCase()}</span>;
  };

  if (loading) return <div className="page-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}><div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /></div>;

  const allBrokerConnections = [...tlConnections, ...mt5Connections, ...tradovateConnections, ...mtConnections, ...volConnections, ...coConnections, ...metaapiAccounts, ...brokerLinks];

  return (
    <div className="page-container">
      <div style={{ padding: '16px 20px' }}><h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '0.1em' }}>PROFILE</h1></div>

      {/* User Card */}
      <div data-testid="profile-user-stats" style={{ margin: '0 16px 16px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: editing ? 16 : 0 }}>
          <div style={{ width: 52, height: 52, background: 'var(--pp-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{(user?.name || 'U')[0].toUpperCase()}</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{user?.name}</p>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--pp-text-3)' }}>{user?.email}</p>
            {user?.phone && !editing && <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--pp-text-3)' }}>{user.phone}</p>}
            {user?.role === 'admin' && <span style={{ fontSize: 9, color: 'var(--pp-primary)', background: 'rgba(155,35,53,0.15)', border: '1px solid rgba(155,35,53,0.3)', padding: '2px 8px', fontWeight: 700, letterSpacing: '0.15em', display: 'inline-block', marginTop: 6 }}>ADMIN</span>}
          </div>
          <button data-testid="edit-profile-btn" onClick={() => { if (!editing) setProfileForm({ name: user?.name || '', phone: user?.phone || '' }); setEditing(!editing); }} style={{ background: 'none', border: '1px solid var(--pp-border)', color: editing ? 'var(--pp-error)' : 'var(--pp-text-3)', fontSize: 11, padding: '5px 12px', cursor: 'pointer', fontWeight: 600 }}>{editing ? 'CANCEL' : 'EDIT'}</button>
        </div>
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div><label style={{ display: 'block', fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--pp-text-3)', marginBottom: 4 }}>NAME</label><input data-testid="edit-name-input" style={{ width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: 14 }} value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><label style={{ display: 'block', fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--pp-text-3)', marginBottom: 4 }}>PHONE</label><input data-testid="edit-phone-input" style={{ width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: 14 }} value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (555) 123-4567" /></div>
            <button data-testid="save-profile-btn" onClick={async () => { try { await api.updateProfile(profileForm); toast.success('Profile updated!'); setEditing(false); window.location.reload(); } catch (e) { toast.error(e.message); } }} style={{ width: '100%', height: 44, background: 'var(--pp-success)', border: 'none', color: '#000', fontWeight: 700, fontSize: 12, cursor: 'pointer', letterSpacing: '0.1em' }}>SAVE CHANGES</button>
          </div>
        )}
      </div>

      {/* Gamification Stats */}
      {userStats && (
        <div style={{ margin: '0 16px 16px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Trophy size={20} color="var(--pp-warning)" />
            <span style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>Your Achievements</span>
            {userStats.current_streak > 0 && (
              <span style={{ background: 'rgba(255,176,0,0.15)', border: '1px solid rgba(255,176,0,0.3)', padding: '3px 10px', fontSize: 12, color: 'var(--pp-warning)', fontWeight: 700 }}>🔥 {userStats.current_streak}</span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
            {[
              { v: userStats.total_copied_trades || 0, l: 'Copies' },
              { v: `$${(userStats.total_profit_copied || 0).toFixed(0)}`, l: 'Profit', color: (userStats.total_profit_copied || 0) >= 0 ? 'var(--pp-success)' : 'var(--pp-error)' },
              { v: userStats.badges?.length || 0, l: 'Badges' },
            ].map(s => (
              <div key={s.l} style={{ textAlign: 'center' }}>
                <p className="mono" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: s.color || 'var(--pp-text)' }}>{s.v}</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>{s.l}</p>
              </div>
            ))}
          </div>
          {userStats.badge_details?.length > 0 && (
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
              {userStats.badge_details.map(b => (
                <div key={b.id} style={{ flexShrink: 0, background: 'var(--pp-overlay)', padding: '8px 12px', textAlign: 'center', minWidth: 70 }}>
                  <div style={{ fontSize: 20 }}>{b.icon}</div>
                  <div style={{ fontSize: 9, color: 'var(--pp-text-3)', marginTop: 4 }}>{b.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ActiveStudent Activation — shown if NOT yet a student */}
      {!user?.is_active_student && <StudentActivationCard onActivated={() => window.location.reload()} />}

      {/* Email Notifications — moved up, more prominent */}
      <button onClick={() => navigate('/notification-preferences')} style={{ margin: '0 16px 12px', width: 'calc(100% - 32px)', background: 'var(--pp-surface)', border: '1px solid rgba(41,121,255,0.35)', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ width: 44, height: 44, background: 'rgba(41,121,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Bell size={24} color="var(--pp-accent)" />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--pp-accent)' }}>Email Notifications</p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>Manage which emails you receive</p>
        </div>
        <ChevronRight size={20} color="var(--pp-accent)" />
      </button>

      {/* Quick Access — Funded Accounts (active students only) + Identity Verification */}

      {/* Funded Accounts — only shown to active students */}
      {user?.is_active_student && (
        <button onClick={() => navigate('/funded-accounts')} style={{ margin: '0 16px 10px', width: 'calc(100% - 32px)', background: 'var(--pp-surface)', border: '1px solid rgba(0,255,148,0.3)', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
          <div style={{ width: 44, height: 44, background: 'rgba(0,255,148,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>💰</div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--pp-success)' }}>Funded Accounts</p>
            <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>View balances · request payouts · drawdown rules</p>
          </div>
          <ChevronRight size={20} color="var(--pp-success)" />
        </button>
      )}

      {/* Identity Verification */}
      <button onClick={() => navigate('/verification')} style={{ margin: '0 16px 10px', width: 'calc(100% - 32px)', background: 'var(--pp-surface)', border: '1px solid rgba(41,121,255,0.3)', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ width: 44, height: 44, background: 'rgba(41,121,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Shield size={24} color="var(--pp-accent)" />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--pp-accent)' }}>
            Identity Verification
            {user?.kyc_verified && <span style={{ marginLeft: 8, fontSize: 10, background: 'rgba(0,255,148,0.15)', color: 'var(--pp-success)', padding: '2px 8px', fontWeight: 700 }}>✓ VERIFIED</span>}
          </p>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>Submit ID · proof of address · selfie</p>
        </div>
        <ChevronRight size={20} color="var(--pp-accent)" />
      </button>

      {onboardingEnabled && (
        <button onClick={() => navigate('/onboarding')} style={{ margin: '0 16px 10px', width: 'calc(100% - 32px)', background: 'var(--pp-primary)', border: 'none', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
          <div style={{ width: 44, height: 44, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontSize: 20 }}>🚀</span>
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#fff', fontWeight: 700 }}>Client Setup Wizard</p>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Connect broker · pick strategies · set risk</p>
          </div>
          <ChevronRight size={20} color="rgba(255,255,255,0.7)" />
        </button>
      )}

      {(user?.role === 'admin' || user?.email === 'trep.trading.co@gmail.com') && (
        <button data-testid="profile-admin-button" onClick={() => navigate('/admin')} style={{ margin: '0 16px 16px', width: 'calc(100% - 32px)', background: 'var(--pp-primary)', border: 'none', padding: 16, display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}>
          <div style={{ width: 44, height: 44, background: 'rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Shield size={24} color="#000" />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#000', fontWeight: 700 }}>Admin Control Panel</p>
            <p style={{ margin: 0, fontSize: 11, color: 'rgba(0,0,0,0.6)', marginTop: 2 }}>Manage strategies, signals, followers, MT5</p>
          </div>
          <ChevronRight size={20} color="rgba(0,0,0,0.5)" />
        </button>
      )}

      {/* Broker Connections */}
      <p data-testid="profile-broker-connections" className="section-label" style={{ padding: '16px 20px 12px' }}>Linked Brokers</p>

      {allBrokerConnections.length === 0 ? (
        <div style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 20, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--pp-text-3)', margin: 0 }}>No brokers linked</p>
        </div>
      ) : (
        <>
          {tlConnections.map((conn, i) => (
            <div key={`tl-${i}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{conn.account_label || 'TradeLocker'}</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>{conn.email} · {conn.environment === 'demo' ? 'DEMO' : 'LIVE'}</p>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-warning)', marginTop: 4 }}>TradeLocker — Full Auto</p>
              </div>
              <StatusBadge status={conn.status || 'active'} />
            </div>
          ))}
          {mt5Connections.map((conn, i) => (
            <div key={`mt5-${i}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{conn.broker_name} — MT5</p>
                <p style={{ margin: 0, fontSize: 11, color: 'var(--pp-text-3)', marginTop: 2 }}>Account: {conn.account_number}</p>
                <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-warning)', marginTop: 4 }}>Semi-Auto (Notifications)</p>
              </div>
              <StatusBadge status={conn.copy_mode || 'active'} />
            </div>
          ))}
          {metaapiAccounts.map((acct, i) => (
            <div key={`mapi-${i}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid rgba(41,121,255,0.3)', borderLeft: '3px solid var(--pp-accent)', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>#{acct.login || acct.account_label || 'MT5 Account'}</p>
                  <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', marginTop: 2 }}>{acct.platform?.toUpperCase() || 'MT5'} · {acct.server}</p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <StatusBadge status={acct.status || 'connecting'} />
                </div>
              </div>
              {acct.status === 'connecting' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <button onClick={async () => { await api.refreshMetaAPIStatus().catch(() => {}); loadData(); }} style={{ fontSize: 10, background: 'rgba(41,121,255,0.12)', border: '1px solid rgba(41,121,255,0.3)', color: 'var(--pp-accent)', padding: '4px 10px', cursor: 'pointer' }}>
                    Refresh Status
                  </button>
                </div>
              )}
              {acct.balance != null && (
                <div style={{ borderTop: '1px solid rgba(39,39,42,0.3)', paddingTop: 10, marginBottom: 8 }}>
                  <p className="section-label" style={{ marginBottom: 4, fontSize: 8 }}>CLOSED BALANCE</p>
                  <p className="mono" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--pp-text)' }}>{acct.currency || 'USD'} {Number(acct.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
              )}
              {/* Disconnect button */}
              <button onClick={async () => {
                if (!window.confirm('Disconnect this MT5 account?')) return;
                await api.disconnectMetaAPISlaveAccount(acct.connection_id).catch(e => toast.error(e.message));
                loadData();
              }} style={{ width: '100%', height: 32, background: 'transparent', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.1em' }}>
                DISCONNECT
              </button>
            </div>
          ))}
          {/* Tradovate */}
          {tradovateConnections.map((conn, i) => (
            <div key={`tv-${i}`} data-testid={`profile-tv-${conn.connection_id || conn.session_id}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid rgba(122,255,0,0.3)', borderLeft: '3px solid #7AFF00', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.account_label || conn.username || 'Tradovate'}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Tradovate · {conn.broker_name || conn.prop_firm_name || 'Tradovate'} · {(conn.environment || 'live').toUpperCase()}</p>
                </div>
                <StatusBadge status={conn.status || 'active'} />
              </div>
              <SubAccountPicker platform="tradovate" connection={conn} onUpdate={loadData} />
              <button onClick={async () => {
                if (!window.confirm('Disconnect this Tradovate account?')) return;
                await api.deleteTradovateConnection(conn.connection_id || conn.session_id).catch(e => toast.error(e.message));
                loadData();
              }} style={{ marginTop: 10, width: '100%', height: 30, background: 'transparent', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', fontSize: 10, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.1em' }}>
                DISCONNECT
              </button>
            </div>
          ))}
          {/* Volumetrica / AquaFutures (Playwright-driven prop platform) */}
          {volConnections.map((conn, i) => (
            <div key={`vol-${i}`} data-testid={`profile-vol-${conn.connection_id}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid rgba(0,201,215,0.3)', borderLeft: '3px solid #00C9D7', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.account_label || conn.email || 'Volumetrica'}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Volumetrica · {conn.prop_firm_name || 'AquaFutures'}</p>
                </div>
                <StatusBadge status={conn.status || 'active'} />
              </div>
              {conn.auth_probe_ok === false && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-warning)' }}>⚠ Probe pending — broker login still being verified.</p>
              )}
              <SubAccountPicker platform="volumetrica" connection={conn} onUpdate={loadData} />
              <button onClick={async () => {
                if (!window.confirm('Disconnect this Volumetrica account?')) return;
                await api.deleteVolumetrica(conn.connection_id).catch(e => toast.error(e.message));
                loadData();
              }} style={{ marginTop: 10, width: '100%', height: 30, background: 'transparent', border: '1px solid rgba(255,51,51,0.3)', color: 'var(--pp-error)', fontSize: 10, fontWeight: 600, cursor: 'pointer', letterSpacing: '0.1em' }}>
                DISCONNECT
              </button>
            </div>
          ))}
          {/* CloseOption (binary broker) */}
          {coConnections.map((conn, i) => (
            <div key={`co-${i}`} data-testid={`profile-co-${conn.connection_id}`} style={{ margin: '0 16px 8px', background: 'var(--pp-surface)', border: '1px solid rgba(255,176,0,0.3)', borderLeft: '3px solid #FFB000', padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.account_label || conn.email || 'CloseOption'}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>CloseOption · Binary Options</p>
                </div>
                <StatusBadge status={conn.status || 'active'} />
              </div>
            </div>
          ))}
        </>
      )}

      <button data-testid="add-broker-btn" onClick={() => navigate('/link-broker')} style={{ margin: '8px 16px', width: 'calc(100% - 32px)', background: 'transparent', border: '1px dashed var(--pp-border)', padding: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: 'var(--pp-primary)' }}>
        <Plus size={18} color="var(--pp-primary)" />
        <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.12em' }}>LINK BROKER ACCOUNT</span>
      </button>

      <div style={{ height: 16 }} />

      {/* Support Section */}
      <p className="section-label" style={{ padding: '0 20px', marginBottom: 12 }}>Support</p>
      <div style={{ margin: '0 16px 16px', display: 'flex', gap: 8 }}>
        <a href="mailto:support@profit-pulse.pro" style={{ flex: 1, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, textAlign: 'center', textDecoration: 'none', cursor: 'pointer' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--pp-accent)', letterSpacing: '0.08em' }}>EMAIL SUPPORT</p>
          <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>support@profit-pulse.pro</p>
        </a>
        <button onClick={() => navigate('/ai?tab=support')} style={{ flex: 1, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14, cursor: 'pointer' }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#00ff88', letterSpacing: '0.08em' }}>LIVE CHAT</p>
          <p style={{ margin: '4px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>Chat with support</p>
        </button>
      </div>

      <button data-testid="logout-btn" onClick={handleLogout} style={{ margin: '0 16px 20px', width: 'calc(100% - 32px)', background: 'transparent', border: '1px solid var(--pp-border)', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer' }}>
        <LogOut size={18} color="var(--pp-error)" />
        <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.12em', color: 'var(--pp-error)' }}>SIGN OUT</span>
      </button>
    </div>
  );
}
